// Shared by the profile page and the dashboard editors. The server side of
// these blocks (validation, crawlable markup) is src/blocks.js and
// src/profileSeo.js.

export const BLOCK_KINDS = ['hours', 'location', 'menu'];
export const DEFAULT_TIMEZONE = 'Europe/Istanbul';

export const emptyHours = () => ({
  timezone: DEFAULT_TIMEZONE,
  note: '',
  days: [0, 1, 2, 3, 4, 5, 6].map(i => (i < 5 ? { closed: false, open: '09:00', close: '18:00' } : { closed: true, open: '', close: '' }))
});

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

// Stored blocks were validated on the way in (src/blocks.js), but one bad
// entry from older data must cost that entry, not the whole page: a day
// without usable times reads as closed, a dish without a name is dropped.
// Kept in step with the same two functions in src/profileView.js.
export const cleanDays = (days) => days.map(day =>
  day && !day.closed && TIME.test(day.open || '') && TIME.test(day.close || '')
    ? day
    : { closed: true, open: '', close: '' });

export const cleanMenuItems = (items) => items
  .filter(item => item && item.name !== undefined && item.name !== null && String(item.name).trim())
  .map(item => {
    const price = item.price === null || item.price === undefined || item.price === '' ? NaN : Number(item.price);
    return {
      name: String(item.name),
      description: item.description ? String(item.description) : '',
      price: isFinite(price) ? price : null
    };
  });

const minutes = hhmm => {
  const [h, m] = String(hhmm).split(':').map(Number);
  return h * 60 + m;
};

// The business's own clock, not the visitor's: a shop in Istanbul is open
// on Istanbul time wherever the page is read from. Returns Monday-first
// weekday index (0-6) and minutes since midnight.
const nowIn = (timezone, date = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone || DEFAULT_TIMEZONE, weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
  }).formatToParts(date);
  const get = type => (parts.find(p => p.type === type) || {}).value;
  const weekday = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(get('weekday'));
  return { weekday, minute: Number(get('hour')) * 60 + Number(get('minute')) };
};

/*
  { open: true, closesAt: '18:00' } or { open: false, opensAt: '09:00', opensDay: 2 }
  A day whose close is earlier than its open runs past midnight, so 22:00-02:00
  on Friday still counts as open at 01:00 on Saturday.
*/
export const openState = (hours, date = new Date()) => {
  if (!hours || !Array.isArray(hours.days) || hours.days.length !== 7) return null;
  const days = cleanDays(hours.days);
  const { weekday, minute } = nowIn(hours.timezone, date);
  const today = days[weekday];
  const yesterday = days[(weekday + 6) % 7];

  if (yesterday && !yesterday.closed && minutes(yesterday.close) < minutes(yesterday.open) && minute < minutes(yesterday.close)) {
    return { open: true, closesAt: yesterday.close };
  }
  if (today && !today.closed) {
    const open = minutes(today.open);
    const close = minutes(today.close);
    const overnight = close < open;
    if (minute >= open && (overnight || minute < close)) return { open: true, closesAt: today.close };
    if (minute < open) return { open: false, opensAt: today.open, opensDay: weekday };
  }
  for (let i = 1; i <= 7; i++) {
    const index = (weekday + i) % 7;
    if (!days[index].closed) return { open: false, opensAt: days[index].open, opensDay: index };
  }
  return { open: false };
};

export const todayIndex = (hours) => nowIn(hours && hours.timezone).weekday;

// ₺ in both languages ("₺1.250,50" / "₺1,250.50"). By hand: Intl's en-US
// output is "TRY 1,250.50", and the server (src/profileView.js) has to
// produce exactly the same string without relying on locale data.
export const formatPrice = (price, lang) => {
  if (price === null || price === undefined || price === '') return '';
  const value = Number(price);
  if (!isFinite(value)) return '';
  const [whole, fraction] = value.toFixed(value % 1 ? 2 : 0).split('.');
  return '₺' + whole.replace(/\B(?=(\d{3})+(?!\d))/g, lang === 'en' ? ',' : '.') + (fraction ? (lang === 'en' ? '.' : ',') + fraction : '');
};

// Neither needs an API key
export const directionsUrl = address => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
export const mapEmbedUrl = address => `https://maps.google.com/maps?q=${encodeURIComponent(address)}&z=15&output=embed`;

// wa.me/<digits>?text=... in both directions, so the dashboard can offer a
// phone field and a ready-made message instead of a raw URL
export const parseWhatsapp = (url) => {
  try {
    const u = new URL(/^https?:\/\//i.test(url) ? url : 'https://' + url);
    const phone = u.hostname.replace(/^www\./, '') === 'wa.me' ? u.pathname.replace(/\D/g, '') : (u.searchParams.get('phone') || '').replace(/\D/g, '');
    return { phone, message: u.searchParams.get('text') || '' };
  } catch (e) {
    return { phone: '', message: '' };
  }
};

export const buildWhatsapp = (phone, message) => {
  let digits = String(phone || '').replace(/\D/g, '');
  // A Turkish number typed the local way (05xx...) needs the country code
  if (/^0\d{10}$/.test(digits)) digits = '90' + digits.slice(1);
  return `https://wa.me/${digits}${message ? `?text=${encodeURIComponent(message)}` : ''}`;
};
